import React, { useEffect, useMemo, useState } from 'react'
import { mentorshipService, type MentorshipLearningGoal, type MentorshipParticipant, type MentorshipSkill, type MentorshipSkillClaim } from '../../../services/mentorship'
import { useI18n } from '../../../i18n/I18nContext'

type Tab =
  | 'dashboard'
  | 'skills'
  | 'approvals'
  | 'learning-goals'
  | 'matches'
  | 'sessions'
  | 'rates'
  | 'bookings'
  | 'payments'
  | 'fees'
  | 'disputes'

const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'skills', label: 'Skills' },
  { id: 'approvals', label: 'Mentor Approvals' },
  { id: 'learning-goals', label: 'Learning Goals' },
  { id: 'matches', label: 'Matches' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'rates', label: 'Mentor Rates' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'payments', label: 'Payment Records' },
  { id: 'fees', label: 'Platform Fees' },
  { id: 'disputes', label: 'Disputes' },
]

const statusClass = (status?: string) => {
  if (['approved', 'accepted', 'active', 'completed', 'confirmed', 'paid', 'recorded', 'paid_off_platform'].includes(String(status))) return 'status-pill status-pill--approved'
  if (['pending', 'requested', 'scheduled', 'proposed', 'in_progress', 'completed_pending_confirmation', 'payable'].includes(String(status))) return 'status-pill status-pill--pending'
  if (['rejected', 'cancelled', 'disputed', 'suspended', 'no_show'].includes(String(status))) return 'status-pill status-pill--rejected'
  return 'status-pill'
}

const titleCase = (value?: string | null) => String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())

const DataTable: React.FC<{
  columns: string[]
  rows: any[]
  empty: string
  renderRow: (row: any) => React.ReactNode
}> = ({ columns, rows, empty, renderRow }) => (
  <div className="admin-table-wrap">
    <table className="admin-table">
      <thead>
        <tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length ? rows.map(renderRow) : (
          <tr><td colSpan={columns.length} className="text-muted">{empty}</td></tr>
        )}
      </tbody>
    </table>
  </div>
)

export const MentorshipPanel: React.FC = () => {
  const { t, formatCurrency, formatNumber } = useI18n()
  const [activeTab, setActiveTab] = useState<Tab>('dashboard')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [skills, setSkills] = useState<MentorshipSkill[]>([])
  const [participants, setParticipants] = useState<MentorshipParticipant[]>([])
  const [claims, setClaims] = useState<MentorshipSkillClaim[]>([])
  const [learningGoals, setLearningGoals] = useState<MentorshipLearningGoal[]>([])
  const [matches, setMatches] = useState<any[]>([])
  const [sessions, setSessions] = useState<any[]>([])
  const [rates, setRates] = useState<any[]>([])
  const [bookings, setBookings] = useState<any[]>([])
  const [payments, setPayments] = useState<any[]>([])
  const [fees, setFees] = useState<any[]>([])
  const [skillDraft, setSkillDraft] = useState({ name: '', category: '', description: '' })
  const [feeDraft, setFeeDraft] = useState({ defaultFeePercent: '', defaultFeeAmount: '', currencyCode: 'CAD' })
  const [busyId, setBusyId] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const [participantRes, skillRes, claimRes, goalRes, matchRes, sessionRes, rateRes, bookingRes, paymentRes, feeRes] = await Promise.all([
        mentorshipService.listParticipants(),
        mentorshipService.listSkills(),
        mentorshipService.listMentorClaims(),
        mentorshipService.listLearningGoals(),
        mentorshipService.listMatches(),
        mentorshipService.listSessions(),
        mentorshipService.listRates(),
        mentorshipService.listBookings(),
        mentorshipService.listPaymentRecords(),
        mentorshipService.listPlatformFees(),
      ])
      setParticipants(participantRes.data?.participants || [])
      setSkills(skillRes.data?.skills || [])
      setClaims(claimRes.data?.claims || [])
      setLearningGoals(goalRes.data?.learningGoals || [])
      setMatches(matchRes.data?.matches || [])
      setSessions(sessionRes.data?.sessions || [])
      setRates(rateRes.data?.rates || [])
      setBookings(bookingRes.data?.bookings || [])
      setPayments(paymentRes.data?.paymentRecords || [])
      setFees(feeRes.data?.platformFeeSettings || [])
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load mentorship data.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const dashboard = useMemo(() => {
    const recorded = payments.filter((payment) => ['paid', 'recorded', 'paid_off_platform'].includes(payment.paymentStatus))
    return [
      { label: 'Pending mentor approvals', value: formatNumber(claims.filter((claim) => claim.status === 'pending').length) },
      { label: 'Active mentors', value: formatNumber(new Set(rates.filter((rate) => rate.isActive).map((rate) => rate.mentorWorkerProfileId)).size) },
      { label: 'Active apprentices', value: formatNumber(learningGoals.filter((goal) => ['active', 'matched'].includes(goal.status)).length) },
      { label: 'Active matches', value: formatNumber(matches.filter((match) => ['accepted', 'active'].includes(match.status)).length) },
      { label: 'Upcoming sessions', value: formatNumber(sessions.filter((session) => session.status === 'scheduled').length) },
      { label: 'Completed sessions', value: formatNumber(sessions.filter((session) => ['confirmed', 'payable', 'paid', 'completed'].includes(session.status)).length) },
      { label: 'Pending bookings', value: formatNumber(bookings.filter((booking) => booking.status === 'requested').length) },
      { label: 'Recorded gross amount', value: formatCurrency(recorded.reduce((sum, payment) => sum + Number(payment.grossAmount || 0), 0)) },
      { label: 'Platform fees recorded', value: formatCurrency(recorded.reduce((sum, payment) => sum + Number(payment.platformFeeAmount || 0), 0)) },
      { label: 'Disputed payments', value: formatNumber(payments.filter((payment) => payment.paymentStatus === 'disputed').length) },
    ]
  }, [bookings, claims, formatCurrency, formatNumber, learningGoals, matches, payments, rates, sessions])

  const createSkill = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!skillDraft.name.trim()) return
    await mentorshipService.createSkill({
      name: skillDraft.name.trim(),
      category: skillDraft.category.trim() || null,
      description: skillDraft.description.trim() || null,
      status: 'active',
    })
    setSkillDraft({ name: '', category: '', description: '' })
    await load()
  }

  const setClaimDecision = async (claimId: string, action: 'approve' | 'reject' | 'suspend') => {
    setBusyId(claimId)
    try {
      if (action === 'approve') await mentorshipService.approveClaim(claimId)
      if (action === 'reject') await mentorshipService.rejectClaim(claimId)
      if (action === 'suspend') await mentorshipService.suspendClaim(claimId)
      await load()
    } finally {
      setBusyId('')
    }
  }

  const updatePayment = async (paymentRecordId: string, paymentStatus: string) => {
    setBusyId(paymentRecordId)
    try {
      await mentorshipService.updatePaymentRecord(paymentRecordId, { paymentStatus })
      await load()
    } finally {
      setBusyId('')
    }
  }

  const createFee = async (event: React.FormEvent) => {
    event.preventDefault()
    await mentorshipService.createPlatformFee({
      defaultFeePercent: feeDraft.defaultFeePercent === '' ? null : Number(feeDraft.defaultFeePercent),
      defaultFeeAmount: feeDraft.defaultFeeAmount === '' ? null : Number(feeDraft.defaultFeeAmount),
      currencyCode: feeDraft.currencyCode || 'CAD',
      isActive: true,
    })
    setFeeDraft({ defaultFeePercent: '', defaultFeeAmount: '', currencyCode: 'CAD' })
    await load()
  }

  if (loading) {
    return (
      <section className="panel workspace-panel stack">
        <h3>{t('Mentorship')}</h3>
        <p className="text-muted">{t('Loading mentorship and apprenticeship records...')}</p>
      </section>
    )
  }

  return (
    <section className="panel workspace-panel stack" data-testid="mentorship-panel">
      <div className="admin-panel-toolbar">
        <div className="stack" style={{ gap: '.25rem' }}>
          <h3 style={{ margin: 0 }}>{t('Mentorship / Apprenticeship')}</h3>
          <p className="text-muted">
            {t('Manage approved mentors, apprentice goals, deterministic matching, sessions, booking requests, internal ledger records, and platform fees.')}
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={load}>{t('Refresh')}</button>
      </div>
      {error ? <p className="error-text">{error}</p> : null}

      <div className="admin-tabs" role="tablist" aria-label={t('Mentorship sections')}>
        {tabs.map((tab) => (
          <button key={tab.id} type="button" className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            {t(tab.label)}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' ? (
        <div className="stack">
          <div className="admin-dashboard-grid">
            {[{ label: 'Mentorship participants', value: formatNumber(participants.length) }, ...dashboard].map((card) => (
              <article key={card.label} className="admin-metric-card">
                <span className="helper">{t(card.label)}</span>
                <strong>{card.value}</strong>
              </article>
            ))}
          </div>
          <DataTable
            columns={['Participant', 'Modes', 'Mentor skills', 'Learning goals', 'Sessions', 'Mentor rating', 'Apprentice rating'].map((column) => t(column))}
            rows={participants}
            empty={t('No mentorship participants yet.')}
            renderRow={(participant) => (
              <tr key={participant.workerProfileId}>
                <td>{participant.workerName || participant.workerProfileId}</td>
                <td>{[
                  participant.mentorModeEnabled ? t('Mentor') : '',
                  participant.apprenticeModeEnabled ? t('Apprentice') : '',
                ].filter(Boolean).join(', ') || t('Activity only')}</td>
                <td>{t('{approved} approved / {claimed} claimed', { approved: formatNumber(participant.approvedMentorSkillCount || 0), claimed: formatNumber(participant.mentorSkillClaimCount || 0) })}</td>
                <td>{formatNumber(participant.learningGoalCount || 0)}</td>
                <td>{t('{count} completed', { count: formatNumber(participant.completedMentorshipSessions || 0) })}</td>
                <td>{participant.mentorReviewCount ? `${participant.mentorRatingAverage} (${formatNumber(participant.mentorReviewCount)})` : t('No mentor reviews')}</td>
                <td>{participant.apprenticeReviewCount ? `${participant.apprenticeRatingAverage} (${formatNumber(participant.apprenticeReviewCount)})` : t('No apprentice reviews')}</td>
              </tr>
            )}
          />
        </div>
      ) : null}

      {activeTab === 'skills' ? (
        <div className="stack">
          <p className="helper">{t('Skills created here are shared with Worker Mentorship and can be selected for worker mentor claims or apprentice learning goals.')}</p>
          <form className="admin-inline-form" onSubmit={createSkill}>
            <input className="input" value={skillDraft.name} onChange={(event) => setSkillDraft((draft) => ({ ...draft, name: event.target.value }))} placeholder={t('Skill name')} />
            <input className="input" value={skillDraft.category} onChange={(event) => setSkillDraft((draft) => ({ ...draft, category: event.target.value }))} placeholder={t('Category')} />
            <input className="input" value={skillDraft.description} onChange={(event) => setSkillDraft((draft) => ({ ...draft, description: event.target.value }))} placeholder={t('Description')} />
            <button type="submit" className="btn btn-primary">{t('Create skill')}</button>
          </form>
          <DataTable
            columns={['Skill', 'Category', 'Description', 'Status'].map((column) => t(column))}
            rows={skills}
            empty={t('No mentorship skills have been created.')}
            renderRow={(skill) => (
              <tr key={skill.id}>
                <td>{skill.name}</td>
                <td>{skill.category || t('Uncategorized')}</td>
                <td>{skill.description || t('No description')}</td>
                <td><span className={statusClass(skill.status)}>{t(titleCase(skill.status))}</span></td>
              </tr>
            )}
          />
        </div>
      ) : null}

      {activeTab === 'approvals' ? (
        <DataTable
          columns={['Worker', 'Claimed skill', 'Evidence', 'Status', 'Actions'].map((column) => t(column))}
          rows={claims}
          empty={t('No mentor skill claims are waiting for review.')}
          renderRow={(claim) => (
            <tr key={claim.id}>
              <td>{claim.workerName || claim.workerProfileId}</td>
              <td>{claim.skillName || claim.skillId}</td>
              <td>{claim.evidenceDocumentId || t('None')}</td>
              <td><span className={statusClass(claim.status)}>{t(titleCase(claim.status))}</span></td>
              <td>
                <div className="button-row">
                  <button type="button" className="btn btn-secondary" disabled={busyId === claim.id} onClick={() => setClaimDecision(claim.id, 'approve')}>{t('Approve')}</button>
                  <button type="button" className="btn btn-secondary" disabled={busyId === claim.id} onClick={() => setClaimDecision(claim.id, 'reject')}>{t('Reject')}</button>
                  <button type="button" className="btn btn-secondary" disabled={busyId === claim.id} onClick={() => setClaimDecision(claim.id, 'suspend')}>{t('Suspend')}</button>
                </div>
              </td>
            </tr>
          )}
        />
      ) : null}

      {activeTab === 'learning-goals' ? (
        <DataTable
          columns={['Apprentice', 'Desired skill', 'Status', 'Suggested mentors', 'Notes'].map((column) => t(column))}
          rows={learningGoals}
          empty={t('No apprentice learning goals yet.')}
          renderRow={(goal) => (
            <tr key={goal.id}>
              <td>{goal.workerName || goal.workerProfileId}</td>
              <td>{goal.skillName || goal.skillId}</td>
              <td><span className={statusClass(goal.status)}>{t(titleCase(goal.status))}</span></td>
              <td>{formatNumber(goal.suggestedMentorCount || 0)}</td>
              <td>{goal.notes || t('None')}</td>
            </tr>
          )}
        />
      ) : null}

      {activeTab === 'matches' ? (
        <DataTable
          columns={['Mentor', 'Apprentice', 'Skill', 'Status', 'Notes'].map((column) => t(column))}
          rows={matches}
          empty={t('No mentorship matches yet.')}
          renderRow={(match) => (
            <tr key={match.id}>
              <td>{match.mentor_worker_profile_id || match.mentorWorkerProfileId}</td>
              <td>{match.apprentice_worker_profile_id || match.apprenticeWorkerProfileId}</td>
              <td>{match.skill_id || match.skillId}</td>
              <td><span className={statusClass(match.status)}>{t(titleCase(match.status))}</span></td>
              <td>{match.notes || t('None')}</td>
            </tr>
          )}
        />
      ) : null}

      {activeTab === 'sessions' ? (
        <DataTable
          columns={['Match', 'Scheduled time', 'Delivery', 'Location', 'Status'].map((column) => t(column))}
          rows={sessions}
          empty={t('No mentorship sessions yet.')}
          renderRow={(session) => (
            <tr key={session.id}>
              <td>{session.match_id || session.matchId}</td>
              <td>{session.scheduled_start_at || session.scheduledStartAt} - {session.scheduled_end_at || session.scheduledEndAt}</td>
              <td>{session.delivery_mode || session.deliveryMode}</td>
              <td>{session.location_id || session.locationId || t('Unassigned')}</td>
              <td><span className={statusClass(session.status)}>{t(titleCase(session.status))}</span></td>
            </tr>
          )}
        />
      ) : null}

      {activeTab === 'rates' ? (
        <DataTable
          columns={['Mentor', 'Skill', 'Rate', 'Currency', 'Unit', 'Active'].map((column) => t(column))}
          rows={rates}
          empty={t('No mentor rates yet.')}
          renderRow={(rate) => (
            <tr key={rate.id}>
              <td>{rate.mentorName || rate.mentorWorkerProfileId}</td>
              <td>{rate.skillName || rate.skillId}</td>
              <td>{rate.rateAmount}</td>
              <td>{rate.currencyCode || 'CAD'}</td>
              <td>{rate.rateUnit}</td>
              <td>{rate.isActive ? t('Active') : t('Inactive')}</td>
            </tr>
          )}
        />
      ) : null}

      {activeTab === 'bookings' ? (
        <DataTable
          columns={['Apprentice', 'Mentor', 'Skill', 'Requested time', 'Proposed rate', 'Status'].map((column) => t(column))}
          rows={bookings}
          empty={t('No booking requests yet.')}
          renderRow={(booking) => (
            <tr key={booking.id}>
              <td>{booking.apprenticeWorkerProfileId}</td>
              <td>{booking.mentorWorkerProfileId}</td>
              <td>{booking.skillName || booking.skillId}</td>
              <td>{booking.requestedStartAt} - {booking.requestedEndAt}</td>
              <td>{formatCurrency(booking.proposedRateAmount, booking.currencyCode)}</td>
              <td><span className={statusClass(booking.status)}>{t(titleCase(booking.status))}</span></td>
            </tr>
          )}
        />
      ) : null}

      {activeTab === 'payments' ? (
        <DataTable
          columns={['Booking', 'Payer', 'Payee', 'Gross', 'Platform fee', 'Mentor net', 'Status', 'Actions'].map((column) => t(column))}
          rows={payments}
          empty={t('No payment records yet.')}
          renderRow={(payment) => (
            <tr key={payment.id}>
              <td>{payment.bookingRequestId}</td>
              <td>{payment.payerWorkerProfileId}</td>
              <td>{payment.payeeWorkerProfileId}</td>
              <td>{formatCurrency(payment.grossAmount, payment.currencyCode)}</td>
              <td>{formatCurrency(payment.platformFeeAmount, payment.currencyCode)}</td>
              <td>{formatCurrency(payment.mentorNetAmount, payment.currencyCode)}</td>
              <td><span className={statusClass(payment.paymentStatus)}>{t(titleCase(payment.paymentStatus))}</span></td>
              <td>
                <div className="button-row">
                  {['paid', 'payable', 'disputed', 'adjusted', 'cancelled'].map((status) => (
                    <button key={status} type="button" className="btn btn-secondary" disabled={busyId === payment.id} onClick={() => updatePayment(payment.id, status)}>{t(titleCase(status))}</button>
                  ))}
                </div>
              </td>
            </tr>
          )}
        />
      ) : null}

      {activeTab === 'fees' ? (
        <div className="stack">
          <form className="admin-inline-form" onSubmit={createFee}>
            <input className="input" type="number" min="0" step="0.01" value={feeDraft.defaultFeePercent} onChange={(event) => setFeeDraft((draft) => ({ ...draft, defaultFeePercent: event.target.value }))} placeholder={t('Default fee percent')} />
            <input className="input" type="number" min="0" step="0.01" value={feeDraft.defaultFeeAmount} onChange={(event) => setFeeDraft((draft) => ({ ...draft, defaultFeeAmount: event.target.value }))} placeholder={t('Default fixed fee')} />
            <input className="input" value={feeDraft.currencyCode} onChange={(event) => setFeeDraft((draft) => ({ ...draft, currencyCode: event.target.value.toUpperCase().slice(0, 3) }))} placeholder="CAD" />
            <button type="submit" className="btn btn-primary">{t('Create tenant default')}</button>
          </form>
          <DataTable
            columns={['Organization', 'Skill', 'Percent', 'Fixed amount', 'Currency', 'Active'].map((column) => t(column))}
            rows={fees}
            empty={t('No platform fee settings yet. Zero platform fee is used by default.')}
            renderRow={(fee) => (
              <tr key={fee.id}>
                <td>{fee.organizationId || t('Tenant default')}</td>
                <td>{fee.skillId || t('All skills')}</td>
                <td>{fee.defaultFeePercent ?? t('None')}</td>
                <td>{fee.defaultFeeAmount ?? t('None')}</td>
                <td>{fee.currencyCode || 'CAD'}</td>
                <td>{fee.isActive ? t('Active') : t('Inactive')}</td>
              </tr>
            )}
          />
        </div>
      ) : null}

      {activeTab === 'disputes' ? (
        <div className="stack">
          <DataTable
            columns={['Type', 'Record', 'Status', 'Resolution'].map((column) => t(column))}
            rows={[
              ...bookings.filter((booking) => booking.status === 'disputed').map((booking) => ({ type: 'Booking', id: booking.id, status: booking.status })),
              ...sessions.filter((session) => session.status === 'disputed').map((session) => ({ type: 'Session', id: session.id, status: session.status })),
              ...payments.filter((payment) => payment.paymentStatus === 'disputed').map((payment) => ({ type: 'Payment', id: payment.id, status: payment.paymentStatus })),
            ]}
            empty={t('No disputed mentorship records.')}
            renderRow={(row) => (
              <tr key={`${row.type}-${row.id}`}>
                <td>{t(row.type)}</td>
                <td>{row.id}</td>
                <td><span className={statusClass(row.status)}>{t(titleCase(row.status))}</span></td>
                <td>{t('Use the owning tab to update status.')}</td>
              </tr>
            )}
          />
        </div>
      ) : null}
    </section>
  )
}
